import React, { useState } from "react";

import { faEllipsis, faFile } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCaretDown, faCaretUp } from "@performyard/fontawesome/pro-solid-svg-icons";
import { AvatarProfile, Chip, DropDownMenu, Icon, Skeleton, Text, Tooltip } from "@performyard/py-components";
import type { DropDownMenuOption, PYColumnDef } from "@performyard/py-components";
import type { WorkflowDetailTodoListItem } from "@performyard/service-contracts";

import { useCurrentUser } from "../../../api/hooks";

import { ROW_HEIGHT, type SortDirection, WorkflowsTableShell, isLoadingRow } from "../WorkflowsTableShell";
import { formatUtcDate } from "../dateFormatting";
import { DETAIL_TODO_STATUS_CONFIG } from "./statuses";

/**
 * A rendered row. The signing wizard creates one to-do per signer, and the API collapses
 * those into a single row carrying its signers in `members`. Expanding one splices those
 * signers in as sibling rows shaped like to-dos, so every cell renderer keeps working —
 * `isMember` is what tells them to drop the parts a signer row doesn't own (the to-do
 * name, and the row actions, which belong to the request as a whole).
 */
type TodoRow = WorkflowDetailTodoListItem & {
	isMember: boolean;
	/**
	 * Grid identity. NOT `_id`: the API's group row reports its first signer's to-do id,
	 * so an expanded group would otherwise contain two rows with the same `_id` —
	 * ag-grid then drops one and the expand toggle stops responding.
	 */
	rowKey: string;
	/**
	 * True for every signer row of an expanded group except its last, so the signers read
	 * as one block instead of a stack of separately-ruled rows. The group row keeps its
	 * divider, which separates its name and assignee count from the signers below it.
	 */
	hidesDivider: boolean;
};

/** Signer rows sit tighter than to-do rows, per the design. */
const MEMBER_ROW_HEIGHT = 40;

/** Just wide enough for the 24px ellipsis trigger plus the grid's own cell padding. */
const ACTIONS_COLUMN_WIDTH = 60;

const isGroup = (row: WorkflowDetailTodoListItem): boolean => row.assigneeCount > 1;

/**
 * Where a to-do's name links, or null to render it as plain text.
 *
 * The signing page serves only the signer it is currently waiting on — every other
 * viewer gets a 403, and a request with nothing left to sign gets a 404 (PY-14127). So
 * the name links to the signed PDF once the request is complete, and to the signing page
 * only while the viewer is the signer it is waiting on. The file icon is rendered either
 * way: it marks the to-do as carrying a signature request, not as one you can open.
 */
const getTodoNameHref = (row: WorkflowDetailTodoListItem, currentUserId: string | undefined): string | null => {
	if (!row.hasSignatureRequest) {
		return null;
	}

	if (row.signatureRequest?.completed) {
		return row.signatureRequest.completedDocumentId
			? `/document/${row.signatureRequest.completedDocumentId}/`
			: null;
	}

	if (!row.signatureRequestId || !currentUserId) {
		return null;
	}

	const activeSigner = isGroup(row)
		? (row.members.find((member) => !member.done)?.actor ?? null)
		: row.done
			? null
			: row.actor;

	return activeSigner?._id === currentUserId ? `/doc-signing/${row.signatureRequestId}/sign/` : null;
};

const buildRows = (todos: WorkflowDetailTodoListItem[], expandedIds: Record<string, boolean>): TodoRow[] =>
	todos.flatMap((todo) => {
		const expanded = isGroup(todo) && !!expandedIds[todo._id];
		const row: TodoRow = { ...todo, isMember: false, hidesDivider: false, rowKey: `group-${todo._id}` };
		if (!expanded) {
			return [row];
		}
		return [
			row,
			...todo.members.map((member, index) => ({
				...todo,
				_id: member._id,
				actor: member.actor,
				dueDate: member.dueDate,
				completedDate: member.completedDate,
				done: member.done,
				status: member.status,
				isMember: true,
				rowKey: `member-${member._id}`,
				hidesDivider: index < todo.members.length - 1,
			})),
		];
	});

type WorkflowDetailsTodosTableProps = {
	todos: WorkflowDetailTodoListItem[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
	/** Whether the current user may mutate to-dos; hides the Actions column when false. */
	canManage: boolean;
	onToggleDone: (todo: WorkflowDetailTodoListItem) => void;
	onEdit: (todo: WorkflowDetailTodoListItem) => void;
	onDelete: (todo: WorkflowDetailTodoListItem) => void;
};

const WorkflowDetailsTodosTable = ({
	todos,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
	canManage,
	onToggleDone,
	onEdit,
	onDelete,
}: WorkflowDetailsTodosTableProps) => {
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id;
	const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
	const rows = buildRows(todos, expandedIds);

	const toggleExpanded = (todoId: string) =>
		setExpandedIds((previous) => ({ ...previous, [todoId]: !previous[todoId] }));

	// `isMember` is a rendering concern; the callbacks feed the edit modal and the
	// mutations, which take a to-do, so it is stripped rather than leaked outward.
	// An action the row can't take is left out rather than shown greyed out; Delete always
	// applies, so the menu is never empty.
	const buildRowOptions = (row: TodoRow): DropDownMenuOption[] => {
		const { isMember: _isMember, hidesDivider: _hidesDivider, rowKey: _rowKey, ...todo } = row;
		const options: DropDownMenuOption[] = [];

		if (!todo.hasSignatureRequest) {
			options.push({
				key: "toggle-done",
				label: todo.done ? "Mark Incomplete" : "Mark Complete",
				onClick: () => onToggleDone(todo),
			});
		}

		if (!todo.done && !isGroup(todo)) {
			options.push({
				key: "edit",
				label: "Edit",
				onClick: () => onEdit(todo),
			});
		}

		options.push({
			key: "delete",
			label: "Delete",
			onClick: () => onDelete(todo),
		});

		return options;
	};

	const columns: PYColumnDef<TodoRow>[] = [
		{
			colId: "action",
			headerName: "To-Do",
			flex: 1,
			minWidth: 220,
			sortable: true,
			cellRenderer: (params: { data: TodoRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				// A signer row sits under the to-do's name; repeating it would read as duplicates.
				if (row.isMember) return null;
				const nameHref = getTodoNameHref(row, currentUserId);

				return (
					<div className="flex min-w-0 items-center gap-1.5">
						<Text size="paragraph-3" weight="regular">
							{nameHref ? (
								<a href={nameHref} className="py-line-clamp-1 text-primary-5 hover:underline">
									{row.action}
								</a>
							) : (
								<span className="py-line-clamp-1">{row.action}</span>
							)}
						</Text>
						{row.hasSignatureRequest && (
							<Tooltip content="Has signature request">
								<span data-testid={`signature-indicator-${row._id}`}>
									<Icon icon={faFile} size={12} color="primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		{
			colId: "actor",
			headerName: "Assignee",
			width: 190,
			valueGetter: (params: { data?: TodoRow }) => params.data?.actor ?? null,
			cellRenderer: (params: { data: TodoRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-24" />;

				// A multi-signer request has no single assignee: it shows a count that expands
				// into the signers, with how many of them are done underneath.
				if (!row.isMember && isGroup(row)) {
					const expanded = !!expandedIds[row._id];
					return (
						<div className="flex flex-col justify-center">
							<button
								type="button"
								aria-expanded={expanded}
								onClick={() => toggleExpanded(row._id)}
								className="flex items-center gap-1.5 border-0 bg-transparent p-0 text-primary-5 hover:cursor-pointer hover:underline"
								data-testid={`assignees-toggle-${row._id}`}
							>
								<Icon icon={expanded ? faCaretUp : faCaretDown} size={12} color="primary-5" />
								<Text size="paragraph-3" weight="regular">
									{`${row.assigneeCount} Assignees`}
								</Text>
							</button>
							<Text size="caption" weight="regular" color="neutral-6">
								{/* Indented past the caret (12px) and its gap (6px) so the counter lines up
								    with the label above it rather than with the caret. */}
								<span className="block ps-[18px]">{`${row.completedCount}/${row.assigneeCount} Complete`}</span>
							</Text>
						</div>
					);
				}

				if (!row.actor) return null;
				return (
					<div className="flex h-full items-center justify-start">
						<AvatarProfile
							employeeId={row.actor._id}
							employeeName={row.actor.name}
							avatarSize={20}
							textSize="paragraph-3"
							textWeight="regular"
							forceHardReload
						/>
					</div>
				);
			},
		},
		{
			colId: "status",
			headerName: "Status",
			width: 140,
			cellRenderer: (params: { data: TodoRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-16" />;
				const { label, chipColor } = DETAIL_TODO_STATUS_CONFIG[row.status];
				return <Chip label={label} color={chipColor} size={24} />;
			},
		},
		{
			colId: "due_date",
			headerName: "Due Date",
			type: "date",
			width: 140,
			sortable: true,
			valueGetter: (params: { data?: TodoRow }) => params.data?.dueDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "date_done",
			headerName: "Completion Date",
			type: "date",
			width: 160,
			sortable: true,
			valueGetter: (params: { data?: TodoRow }) => params.data?.completedDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		...(canManage
			? [
					{
						colId: "actions",
						headerName: "",
						width: ACTIONS_COLUMN_WIDTH,
						maxWidth: ACTIONS_COLUMN_WIDTH,
						cellClass: "py-table-dropdown-cell",
						cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
						cellRenderer: (params: { data: TodoRow }) => {
							const row = params.data;
							if (isLoadingRow(row)) return <Skeleton className="h-6 w-6" />;
							// The menu acts on the whole request, so it lives on the group row only.
							if (row.isMember) return null;
							return (
								<DropDownMenu
									options={buildRowOptions(row)}
									placement="bottom-end"
									testid={`todo-actions-${row._id}`}
									className="group flex items-center"
								>
									<Icon
										icon={faEllipsis}
										size={24}
										color="neutral-7"
										dangerouslySetClassName="transition-colors group-hover:text-neutral-9 group-active:text-neutral-9"
									/>
									<span className="sr-only">{`Actions for ${row.action}`}</span>
								</DropDownMenu>
							);
						},
					} as PYColumnDef<TodoRow>,
				]
			: []),
	];

	return (
		<WorkflowsTableShell<TodoRow>
			testId="workflow-details-todos-table"
			columns={columns}
			rows={rows}
			isLoading={isLoading}
			onSortChange={onSortChange}
			activeSort={activeSort}
			activeSortDirection={activeSortDirection}
			emptyStateText={emptyStateText}
			getRowHeight={(row) => (row.isMember ? MEMBER_ROW_HEIGHT : ROW_HEIGHT)}
			getRowClass={(row) => (row.hidesDivider ? "!border-b-0" : undefined)}
			getRowId={(row) => row.rowKey}
		/>
	);
};

export default WorkflowDetailsTodosTable;
